import { format, isValid, parseISO } from 'date-fns';
import { nanoidGenerator } from 'features/common/helpers';
import { IPassenger, IPassengerForm, PassengerDocumentEnum } from 'features/salesOrder/types';
import { mapPassengerDocumentsForApi } from 'pages/SalesOrder/SalesOrderScreen/components/Paxes/helpers';

/** Ej. "LIXO" / "baudean" → "Lixo" / "Baudean" (por palabra). */
export const formatPersonName = (value: string): string =>
	value
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(' ');

const toDateInputValue = (value: string | undefined): string => {
	if (!value) {
		return '';
	}
	if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
		return value.trim();
	}
	const parsed = parseISO(value);
	if (isValid(parsed)) {
		return format(parsed, 'yyyy-MM-dd');
	}
	const fallback = new Date(value);
	return isValid(fallback) ? format(fallback, 'yyyy-MM-dd') : '';
};

const normalizePassengerFormForSave = (form: IPassengerForm): IPassengerForm => ({
	...form,
	name: formatPersonName(form.name),
	lastName: formatPersonName(form.lastName),
	birthdate: toDateInputValue(form.birthdate),
	documents: form.documents.map((doc) => ({
		...doc,
		country: doc.country?.trim().toUpperCase() || doc.country,
		expirationDate: toDateInputValue(doc.expirationDate),
	})),
});

export interface SavePassengerInput {
	form: IPassengerForm;
	countryCode: string;
	isMainPassenger: boolean;
}

/** Payload mínimo para `updatePax` (viajero físico no facturable + documentos). */
export const buildPassengerPayloadForOrder = ({
	form,
	countryCode,
	isMainPassenger,
}: SavePassengerInput): IPassenger => {
	const normalizedForm = normalizePassengerFormForSave({
		...form,
		mainPassenger: isMainPassenger,
	});
	const marketCountry = countryCode === 'TN' ? 'PY' : countryCode;

	return {
		name: normalizedForm.name,
		lastName: normalizedForm.lastName,
		address: normalizedForm.address || '',
		countryId: 1,
		birthdate: normalizedForm.birthdate,
		phone: normalizedForm.phone,
		email: normalizedForm.email,
		frequentFlyerNumbers: normalizedForm.frequentFlyerNumbers || [],
		id: normalizedForm.id,
		keyId: normalizedForm.keyId,
		isPhysicalPerson: true,
		invoiceable: false,
		mainPassenger: isMainPassenger,
		documents: mapPassengerDocumentsForApi(normalizedForm.documents, marketCountry),
	} as IPassenger;
};

export const createEmptyPassengerForm = ({
	keyId,
	documentCountryCode,
	documentType,
	documentNumber,
	isMainPassenger,
}: {
	keyId: string;
	/** ISO de 2 letras (ej. UY). */
	documentCountryCode: string;
	documentType: PassengerDocumentEnum;
	documentNumber: string;
	isMainPassenger: boolean;
}): IPassengerForm => ({
	id: '',
	keyId,
	name: '',
	lastName: '',
	state: '',
	province: '',
	municipality: '',
	birthdate: '',
	isPhysicalPerson: true,
	invoiceable: false,
	mainPassenger: isMainPassenger,
	countryId: '1',
	postalCode: '',
	address: '',
	syncStatus: null,
	documents: [
		{
			keyId: nanoidGenerator(),
			documentType,
			country: documentCountryCode,
			documentNumber,
			expirationDate: '',
		},
	],
	frequentFlyerNumbers: [],
});

export const mapAutocompleteToPassengerForm = (
	existing: IPassenger,
	keyId: string,
	isMainPassenger: boolean,
	marketCountryCode: string,
): IPassengerForm => ({
	id: existing.id || '',
	keyId,
	name: existing.name || '',
	lastName: existing.lastName || '',
	state: existing.state || '',
	province: existing.province || '',
	municipality: existing.municipality || '',
	birthdate: toDateInputValue(existing.birthdate),
	isPhysicalPerson: existing.isPhysicalPerson ?? true,
	invoiceable: existing.invoiceable ?? false,
	mainPassenger: isMainPassenger,
	countryId: '1',
	postalCode: '',
	address: '',
	syncStatus: null,
	documents:
		existing.documents?.map((doc) => {
			const rawCountry = typeof doc.country === 'string' ? doc.country : doc.country.code;
			const country = /^[A-Za-z]{2}$/.test(rawCountry) ? rawCountry.toUpperCase() : marketCountryCode;
			return {
				keyId: doc.keyId || nanoidGenerator(),
				documentType: doc.documentType as unknown as PassengerDocumentEnum,
				country,
				documentNumber: doc.documentNumber,
				expirationDate: toDateInputValue(doc.expirationDate),
				image: doc.image,
			};
		}) || [],
	frequentFlyerNumbers: existing.frequentFlyerNumbers || [],
	phone: existing.phone,
	email: existing.email,
	documentType: existing.documentType,
	documentNumber: existing.documentNumber,
	documentVerificationNumber: existing.documentVerificationNumber,
});
